/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import type {
  AgentCapabilityConfirmation,
  AgentCapabilityEffect,
  AgentWriteSemantics,
} from '@poweralpha/common';
import * as z from 'zod';

/**
 * Exposes a definition to the PA Agent: its value goes with each agent turn (current value for
 * state, the live form's values for drafts) and, when `writable`, the agent can set it. Only
 * definitions with this option are visible to the agent.
 */
export interface DesktopAgentOption {
  /** What the value is, for the agent. */
  description: string;
  /** Short name shown when the agent changes it, e.g. "PVSim config inputs". */
  label: string;
  writable: boolean;
  /** Window app ids the definition belongs to; defaults to the key's namespace (`pvsim.x`). */
  apps?: readonly string[];
  /**
   * Schema the agent gets as JSON Schema, when the definition's own schema is too loose to
   * describe the value (e.g. a draft typed with `z.custom`). Agent writes must also pass it
   * structurally (right types and shapes) before they are applied.
   */
  jsonSchema?: z.ZodType;
  /** What a write does; defaults to `draft_edit` (unsaved form edits). */
  effect?: AgentCapabilityEffect;
  /** How agent writes merge; defaults match a form (see `applyMergePatch`). */
  writeSemantics?: AgentWriteSemantics;
  /** Required for `confirmation_request`: the dialog prompt and what confirming does. */
  confirmation?: AgentCapabilityConfirmation;
  /** Bump when the value's shape or meaning changes; defaults to `<key>@1`. */
  contractVersion?: string;
}

/**
 * A piece of desktop window state, declared once per app (e.g. in `<app>/windowState.ts`).
 * The schema types it and checks what comes back from the server: a value saved under an older
 * shape reads as `initial` instead.
 */
export interface DesktopStateDefinition<T> {
  kind: 'state';
  /** Namespaced by app, e.g. `pvsim.tabSearch`. */
  key: string;
  /** `window`: one value for the whole window; `view`: one per view (path and search). */
  scope: 'view' | 'window';
  schema: z.ZodType<T>;
  initial: T;
  agent?: DesktopAgentOption;
}

/**
 * Unsaved edits of a form, per view. Drafts may be invalid mid-edit, so `schema` is usually a
 * loose version of the form schema (or `z.custom<Values>()`); bump `version` whenever the form's
 * values change shape, which drops older drafts.
 */
export interface DesktopDraftDefinition<T> {
  kind: 'draft';
  key: string;
  version: number;
  schema: z.ZodType<T>;
  agent?: DesktopAgentOption;
}

type DesktopDefinition = DesktopStateDefinition<unknown> | DesktopDraftDefinition<unknown>;

const definitionsByKey = new Map<string, DesktopDefinition>();

// Keys are the storage identity, so two definitions must never share one.
const register = <T extends DesktopDefinition>(definition: T): T => {
  const existing = definitionsByKey.get(definition.key);
  if (existing && existing !== definition) {
    // Hot reloads re-create definitions with the same key; only warn.
    // biome-ignore lint/suspicious/noConsole: a duplicate key is a programming error to surface
    console.error(`Desktop state key "${definition.key}" is defined twice.`);
  }
  definitionsByKey.set(definition.key, definition);
  return definition;
};

export const defineDesktopState = <S extends z.ZodType>({
  key,
  scope = 'window',
  schema,
  initial,
  agent,
}: {
  key: string;
  scope?: 'view' | 'window';
  schema: S;
  initial: z.output<S>;
  agent?: DesktopAgentOption;
}): DesktopStateDefinition<z.output<S>> =>
  register({
    kind: 'state',
    key,
    scope,
    schema: schema as z.ZodType<z.output<S>>,
    initial,
    agent,
  });

export const defineDesktopDraft = <S extends z.ZodType>({
  key,
  version,
  schema,
  agent,
}: {
  key: string;
  version: number;
  schema: S;
  agent?: DesktopAgentOption;
}): DesktopDraftDefinition<z.output<S>> =>
  register({ kind: 'draft', key, version, schema: schema as z.ZodType<z.output<S>>, agent });

/** A registered definition exposed to the agent (`agent` option), by key. */
export const getAgentDefinition = (key: string) => {
  const definition = definitionsByKey.get(key);
  return definition?.agent ? definition : undefined;
};

/** Definitions exposed to the agent for windows of `appId`. */
export const listAgentDefinitions = (appId: string) =>
  [...definitionsByKey.values()].filter(
    (definition) =>
      definition.agent && (definition.agent.apps ?? [definition.key.split('.')[0]]).includes(appId),
  );

/** Decoded value of a definition; `initial` when missing or saved under another shape. */
export const decodeDesktopState = <T>(definition: DesktopStateDefinition<T>, raw: unknown): T => {
  if (raw === undefined) return definition.initial;
  const parsed = definition.schema.safeParse(raw);
  return parsed.success ? parsed.data : definition.initial;
};

/** Drafts are stored with their definition's version. */
export interface StoredDraft {
  v: number;
  values: unknown;
}

/**
 * Stores only what the schema describes: object schemas drop fields they do not declare (e.g. the
 * rest of a selected project). A value the schema rejects mid-edit is stored as is.
 */
export const encodeDesktopDraft = <T>(definition: DesktopDraftDefinition<T>, values: T) => {
  const parsed = definition.schema.safeParse(values);
  return {
    v: definition.version,
    values: parsed.success ? parsed.data : values,
  } satisfies StoredDraft;
};

/** The draft's values; `undefined` when missing, from another version or saved under another shape. */
export const decodeDesktopDraft = <T>(
  definition: DesktopDraftDefinition<T>,
  raw: unknown,
): T | undefined => {
  const stored = raw as Partial<StoredDraft> | undefined;
  if (!stored || stored.v !== definition.version) return undefined;
  const parsed = definition.schema.safeParse(stored.values);
  return parsed.success ? parsed.data : undefined;
};

export const listDraftKeys = () =>
  [...definitionsByKey.values()]
    .filter((definition) => definition.kind === 'draft')
    .map(({ key }) => key);

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

// The value with the string at `path` turned into a date; the same object when there is none.
const reviveDateAt = (value: unknown, [key, ...rest]: string[]): unknown => {
  if (!isObject(value) || !(key in value)) return value;
  const item = value[key];
  if (rest.length) {
    const next = reviveDateAt(item, rest);
    return next === item ? value : { ...value, [key]: next };
  }
  return typeof item === 'string' ? { ...value, [key]: new Date(item) } : value;
};

/**
 * Loose schema of form values (`defineDesktopDraft`): drafts can be invalid mid-edit, so only the
 * shape is checked. Dates come back from JSON as strings; `datePaths` (dotted) become dates again
 * for the form's checks.
 */
export const formValuesSchema = <T>(datePaths: readonly string[] = []) =>
  z
    .custom<T>(isObject)
    .transform(
      (values) =>
        datePaths.reduce<unknown>((at, path) => reviveDateAt(at, path.split('.')), values) as T,
    );
